import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pencil, Plus, Trash2, X, Star, Package } from 'lucide-react';
import { toast } from 'sonner';

interface Pkg {
  id: string;
  name: string;
  tier_label: string | null;
  audience: 'individual' | 'agency' | 'developer';
  price_etb: number;
  price_currency: 'ETB' | 'USD';
  duration_days: number;
  billing_cycle: string;
  perks: string[];
  // typed credits
  listing_quota: number;
  project_quota: number;
  hot_credits: number;
  super_hot_credits: number;
  refresh_credits: number;
  story_ad_credits: number;
  allows_banner_ads: boolean;
  banner_ad_quota: number;
  search_boost_tier: 'none' | 'standard' | 'hot' | 'super_hot';
  featured_listing_quota: number;
  cta_mode: 'inquire';
  is_active: boolean;
  is_popular: boolean;
  sort_order: number;
  description: string | null;
}

const PERK_PRESETS = [
  'Titanium Badge', 'Titanium Plus Badge', 'Priority Listing Approval',
  'Featured Agency', 'Featured Developers', 'Exclusive Agency Profile',
  'Video Interview', '24/7 Normal Support', '24/7 Priority Support',
  'Developer Pages and Listing',
];

const emptyForm = (): Pkg => ({
  id: '', name: '', tier_label: '', audience: 'agency',
  price_etb: 0, price_currency: 'ETB', duration_days: 30, billing_cycle: 'monthly_billed_annually',
  perks: [],
  listing_quota: 0, project_quota: 0,
  hot_credits: 0, super_hot_credits: 0, refresh_credits: 0, story_ad_credits: 0,
  allows_banner_ads: false, banner_ad_quota: 0,
  search_boost_tier: 'none', featured_listing_quota: 0,
  cta_mode: 'inquire', is_active: true, is_popular: false, sort_order: 100, description: '',
});

export function AdvertisingPackagesManager() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'agency' | 'developer' | 'individual'>('agency');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Pkg>(emptyForm());
  const [newPerk, setNewPerk] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: packages, isLoading } = useQuery({
    queryKey: ['ad-packages-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('advertising_packages' as any)
        .select('*')
        .order('audience').order('sort_order');
      if (error) throw error;
      return (data as unknown as Pkg[]) || [];
    },
  });

  const filtered = (packages || []).filter((p) => p.audience === tab);

  const openNew = () => { setForm({ ...emptyForm(), audience: tab }); setOpen(true); };
  const openEdit = (p: Pkg) => {
    setForm({
      ...emptyForm(),
      ...p,
      tier_label: p.tier_label || '',
      perks: Array.isArray(p.perks) ? p.perks : [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload: any = {
      name: form.name.trim(),
      tier_label: form.tier_label?.trim() || null,
      audience: form.audience,
      price_etb: Number(form.price_etb) || 0,
      price_currency: form.price_currency,
      duration_days: Number(form.duration_days) || 30,
      billing_cycle: form.billing_cycle,
      perks: form.perks,
      listing_quota: Number(form.listing_quota) || 0,
      project_quota: Number(form.project_quota) || 0,
      hot_credits: Number(form.hot_credits) || 0,
      super_hot_credits: Number(form.super_hot_credits) || 0,
      refresh_credits: Number(form.refresh_credits) || 0,
      story_ad_credits: Number(form.story_ad_credits) || 0,
      allows_banner_ads: form.allows_banner_ads,
      banner_ad_quota: Number(form.banner_ad_quota) || 0,
      search_boost_tier: form.search_boost_tier || 'none',
      featured_listing_quota: Number(form.featured_listing_quota) || 0,
      cta_mode: 'inquire',
      is_active: form.is_active,
      is_popular: form.is_popular,
      sort_order: Number(form.sort_order) || 100,
      description: form.description || null,
    };
    const { error } = form.id
      ? await supabase.from('advertising_packages' as any).update(payload).eq('id', form.id)
      : await supabase.from('advertising_packages' as any).insert(payload);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(form.id ? 'Package updated' : 'Package created');
    setOpen(false);
    qc.invalidateQueries({ queryKey: ['ad-packages-admin'] });
    qc.invalidateQueries({ queryKey: ['advertising-packages-public'] });
  };

  const toggleActive = async (p: Pkg) => {
    const { error } = await supabase.from('advertising_packages' as any)
      .update({ is_active: !p.is_active }).eq('id', p.id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ['ad-packages-admin'] });
    qc.invalidateQueries({ queryKey: ['advertising-packages-public'] });
  };

  const togglePopular = async (p: Pkg) => {
    const { error } = await supabase.from('advertising_packages' as any)
      .update({ is_popular: !p.is_popular }).eq('id', p.id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ['ad-packages-admin'] });
  };

  const remove = async (p: Pkg) => {
    if (!confirm(`Archive "${p.name}"?`)) return;
    const { error } = await supabase.from('advertising_packages' as any)
      .update({ is_active: false }).eq('id', p.id);
    if (error) return toast.error(error.message);
    toast.success('Archived');
    qc.invalidateQueries({ queryKey: ['ad-packages-admin'] });
  };

  const addPerk = (val?: string) => {
    const v = (val ?? newPerk).trim();
    if (!v || form.perks.includes(v)) return;
    setForm({ ...form, perks: [...form.perks, v] });
    setNewPerk('');
  };
  const removePerk = (v: string) => setForm({ ...form, perks: form.perks.filter((x) => x !== v) });

  const NumField = ({ label, k }: { label: string; k: keyof Pkg }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" min={0} value={form[k] as number}
        onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) } as any)} />
    </div>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" /> Advertising Packages
          </CardTitle>
          <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" /> New Package</Button>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
            <TabsList>
              <TabsTrigger value="agency">Agency</TabsTrigger>
              <TabsTrigger value="developer">Developer</TabsTrigger>
              <TabsTrigger value="individual">Individual</TabsTrigger>
            </TabsList>
            <TabsContent value={tab} className="mt-4">
              {isLoading ? <Skeleton className="h-48 w-full" /> : filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No packages yet for {tab}.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead className="text-xs">Listings</TableHead>
                      <TableHead className="text-xs">Projects</TableHead>
                      <TableHead className="text-xs">Hot / SH / Refr / Story</TableHead>
                      <TableHead className="text-xs">Banner</TableHead>
                      <TableHead className="text-xs">Boost</TableHead>
                      <TableHead>Pop</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>
                          <div className="font-medium">{p.name}</div>
                          {p.tier_label && <div className="text-xs text-muted-foreground">{p.tier_label}</div>}
                        </TableCell>
                        <TableCell>{p.price_currency} {Number(p.price_etb).toLocaleString()}</TableCell>
                        <TableCell className="text-xs">{p.listing_quota}</TableCell>
                        <TableCell className="text-xs">{p.project_quota}</TableCell>
                        <TableCell className="text-xs tabular-nums">
                          {p.hot_credits}/{p.super_hot_credits}/{p.refresh_credits}/{p.story_ad_credits}
                        </TableCell>
                        <TableCell className="text-xs">{p.allows_banner_ads ? p.banner_ad_quota : '—'}</TableCell>
                        <TableCell className="text-xs">
                          {p.search_boost_tier !== 'none'
                            ? <Badge variant="outline" className="text-[10px] capitalize">{p.search_boost_tier.replace('_',' ')}·{p.featured_listing_quota}</Badge>
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell><Switch checked={p.is_popular} onCheckedChange={() => togglePopular(p)} /></TableCell>
                        <TableCell><Switch checked={p.is_active} onCheckedChange={() => toggleActive(p)} /></TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(p)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit Package' : 'New Package'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Basics */}
            <div>
              <p className="text-sm font-semibold mb-2">Basics</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Internal name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Tier label (shown to user)</Label>
                  <Input value={form.tier_label || ''} onChange={(e) => setForm({ ...form, tier_label: e.target.value })} placeholder="e.g. Titanium Plus" />
                </div>
                <div>
                  <Label className="text-xs">Audience</Label>
                  <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v as any })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agency">Agency</SelectItem>
                      <SelectItem value="developer">Developer</SelectItem>
                      <SelectItem value="individual">Individual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Sort order</Label>
                  <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
                </div>
                <div>
                  <Label className="text-xs">Price</Label>
                  <Input type="number" value={form.price_etb} onChange={(e) => setForm({ ...form, price_etb: Number(e.target.value) })} />
                </div>
                <div>
                  <Label className="text-xs">Currency</Label>
                  <Select value={form.price_currency} onValueChange={(v) => setForm({ ...form, price_currency: v as any })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ETB">ETB</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Duration (days)</Label>
                  <Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: Number(e.target.value) })} />
                </div>
                <div>
                  <Label className="text-xs">Billing cycle label</Label>
                  <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Per Month</SelectItem>
                      <SelectItem value="monthly_billed_annually">Per Month (Billed Annually)</SelectItem>
                      <SelectItem value="annually">Per Year</SelectItem>
                      <SelectItem value="one_time">One Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 flex items-center gap-6">
                  <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label className="text-xs">Active</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={form.is_popular} onCheckedChange={(v) => setForm({ ...form, is_popular: v })} /><Label className="text-xs">Most Popular</Label></div>
                </div>
              </div>
            </div>

            {/* What it includes */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold mb-2">What it includes <span className="text-xs font-normal text-muted-foreground">(0 = not included)</span></p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <NumField label="Listings" k="listing_quota" />
                <NumField label="Projects" k="project_quota" />
                <NumField label="Hot Credits" k="hot_credits" />
                <NumField label="Super Hot Credits" k="super_hot_credits" />
                <NumField label="Refresh Credits" k="refresh_credits" />
                <NumField label="Story Ads" k="story_ad_credits" />
                <NumField label="Boost Slots" k="featured_listing_quota" />
                <div>
                  <Label className="text-xs">Boost Tier</Label>
                  <Select value={form.search_boost_tier} onValueChange={(v) => setForm({ ...form, search_boost_tier: v as any })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="hot">Hot</SelectItem>
                      <SelectItem value="super_hot">Super Hot</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t">
                <div className="flex items-center gap-2">
                  <Switch checked={form.allows_banner_ads} onCheckedChange={(v) => setForm({ ...form, allows_banner_ads: v })} />
                  <Label className="text-xs">Includes Banner Advertising</Label>
                </div>
                <div>
                  <Label className="text-xs">Banner ads included</Label>
                  <Input type="number" disabled={!form.allows_banner_ads} value={form.banner_ad_quota}
                    onChange={(e) => setForm({ ...form, banner_ad_quota: Number(e.target.value) })} />
                </div>
              </div>
            </div>

            {/* Perks */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold mb-2">Perks <span className="text-xs font-normal text-muted-foreground">(also-includes bullets)</span></p>
              <div className="flex flex-wrap gap-2">
                {form.perks.map((p) => (
                  <Badge key={p} variant="default" className="gap-1">
                    {p} <button onClick={() => removePerk(p)}><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {PERK_PRESETS.filter((p) => !form.perks.includes(p)).map((p) => (
                  <Button key={p} size="sm" variant="outline" onClick={() => addPerk(p)}>+ {p}</Button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input placeholder="Custom perk" value={newPerk} onChange={(e) => setNewPerk(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPerk(); } }} />
                <Button onClick={() => addPerk()}><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>
              {form.is_popular && <Star className="h-3.5 w-3.5 mr-1" />}
              {form.id ? 'Save changes' : 'Create package'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
